import React, { useState, useEffect } from 'react';
import { Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import moment from 'moment';
import ShareLicenseModal, { LicenseDataWithShare } from './ShareLicenseModal';
import LicenseHistoryModal from './LicenseHistoryModal';
import ClientLicenseHistoryModal from './ClientLicenseHistoryModal';
import { useOrganizationStore } from '../../store/organization.store';

interface AudioFile {
  fileName: string;
  storageRef: string;
  downloadUrl?: string;
  fileType?: string;
}

interface LicenseField {
  label: string;
  value: string;
}

interface License {
  id: string;
  status: string;
  licenseType: string;
  dateExpires: number | null;
  dateStart: number | null;
  createdAt: number;
  clientOrgId: string | null;
  claimCode: string | null;
  jobTitle?: string;
  client?: LicenseField | null;
  agency?: LicenseField | null;
  clientOrgName?: string;
}

interface Track {
  id: string;
  trackName: string;
  licenseStatus?: {
    isLicensed: boolean;
    durationType: string;
    expiryDate?: number;
    startDate?: number;
  };
  dateExpires?: number;
  // Track metadata for pre-filling license
  campaign?: string;
  jobTitle?: string;
  client?: { label: string; value: string };
  agency?: { label: string; value: string };
  producer?: string | { label: string; value: string };
  creativedirector?: any;
  creativeDirector?: any;
  flightingplatform?: any[];
  flightingPlatform?: any[];
  // Audio files for playback
  mp3Files?: AudioFile[];
  wavFiles?: AudioFile[];
  defaultMp3Id?: string;
  // Track metadata for client display
  key?: { label: string; value: string } | null;
  bpm?: { label: string; value: string } | null;
  genre?: { label: string; value: string } | null;
  tags?: { label: string; value: string }[];
  composer?: { label: string; value: string }[];
  artist?: { label: string; value: string } | null;
  album?: { label: string; value: string } | null;
  recordLabel?: { label: string; value: string } | null;
  publisher?: { label: string; value: string } | null;
  sessionArtists?: { label: string; value: string }[];
  trackColor?: string | null;
  // Licensed track fields (for tracks licensed TO this org)
  isLicensedTrack?: boolean;
  licensePath?: string;
  sourceOrgId?: string;
  sourceOrgName?: string;
  sourceTrackId?: string;
  licenseId?: string;
}

interface LicenseStatusSelectorProps {
  track: Track;
  updateTrackLicense: (trackId: string, updates: any) => void;
  firebase: any;
}

const LicenseStatusSelector: React.FC<LicenseStatusSelectorProps> = ({
  track,
  updateTrackLicense,
  firebase,
}) => {
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showClientHistoryModal, setShowClientHistoryModal] = useState(false);
  const [showClaimLinkModal, setShowClaimLinkModal] = useState(false);
  const [claimLink, setClaimLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // State for all licenses for this track (for owned tracks)
  const [licenses, setLicenses] = useState<License[]>([]);
  const [licensesLoading, setLicensesLoading] = useState(false);

  // State for client licenses (for licensed tracks)
  const [clientLicenses, setClientLicenses] = useState<License[]>([]);
  const [clientLicensesLoading, setClientLicensesLoading] = useState(false);

  const { currentOrgId, currentOrgName } = useOrganizationStore();

  // Helper to check if a license is active
  const isLicenseActive = (license: License) => {
    if (license.status === 'inactive') return false;
    if (license.dateExpires && license.dateExpires < Date.now()) return false;
    return true;
  };

  // Real-time listener for licenses (for owned tracks)
  useEffect(() => {
    if (track.isLicensedTrack || !currentOrgId || !firebase) {
      return;
    }

    setLicensesLoading(true);
    const unsubscribe = firebase.subscribeLicensesForTrack(
      currentOrgId,
      track.id,
      (fetchedLicenses: License[]) => {
        setLicenses(fetchedLicenses);
        setLicensesLoading(false);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [track.isLicensedTrack, track.id, currentOrgId, firebase]);

  // Real-time listener for client licenses (for licensed tracks)
  useEffect(() => {
    if (!track.isLicensedTrack || !track.sourceOrgId || !currentOrgId || !firebase) {
      return;
    }

    setClientLicensesLoading(true);
    const trackId = track.sourceTrackId || track.id;
    const unsubscribe = firebase.subscribeLicensesForClient(
      track.sourceOrgId,
      trackId,
      currentOrgId,
      (fetchedLicenses: License[]) => {
        setClientLicenses(fetchedLicenses);
        setClientLicensesLoading(false);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [track.isLicensedTrack, track.sourceOrgId, track.sourceTrackId, track.id, currentOrgId, firebase]);

  // For licensed tracks (tracks licensed TO this org), show status based on all client licenses
  if (track.isLicensedTrack) {
    // Get active licenses for this client
    const activeClientLicenses = clientLicenses.filter(isLicenseActive);

    // Get the most recent active license
    const mostRecentActiveLicense = activeClientLicenses.length > 0
      ? activeClientLicenses.sort((a, b) => b.createdAt - a.createdAt)[0]
      : null;

    const hasActiveLicense = mostRecentActiveLicense !== null;
    const expiryDate = mostRecentActiveLicense?.dateExpires;
    const licenseType = mostRecentActiveLicense?.licenseType || 'none';
    const formattedExpiry = expiryDate ? moment(expiryDate).format('DD/MM/YYYY') : '';

    const handleClientBadgeClick = () => {
      setShowClientHistoryModal(true);
    };

    return (
      <div className="license-status-selector d-flex">
        {clientLicensesLoading ? (
          <small className="text-muted">Loading...</small>
        ) : hasActiveLicense ? (
          <div className="d-flex flex-column align-items-center">
            <Badge
              className="badge-accent"
              style={{ cursor: 'pointer' }}
              onClick={handleClientBadgeClick}
              title="Click to view license history"
            >
              Active
            </Badge>
            <div className="mt-1">
              <small className="text-white">
                {licenseType === 'blanket' ? 'Blanket' : expiryDate ? `Until ${formattedExpiry}` : ''}
              </small>
            </div>
            {track.sourceOrgName && (
              <div className="mt-1">
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                  From: {track.sourceOrgName}
                </small>
              </div>
            )}
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center">
            <Badge
              className="badge-inactive"
              style={{ cursor: 'pointer' }}
              onClick={handleClientBadgeClick}
              title="Click to view license history"
            >
              Inactive
            </Badge>
            {track.sourceOrgName && (
              <div className="mt-1">
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                  From: {track.sourceOrgName}
                </small>
              </div>
            )}
          </div>
        )}

        {/* Client License History Modal */}
        <ClientLicenseHistoryModal
          isOpen={showClientHistoryModal}
          onClose={() => setShowClientHistoryModal(false)}
          trackName={track.trackName}
          trackId={track.sourceTrackId || track.id}
          sourceOrgId={track.sourceOrgId || ''}
          sourceOrgName={track.sourceOrgName}
          licenses={clientLicenses}
          isLoading={clientLicensesLoading}
          firebase={firebase}
        />
      </div>
    );
  }

  // ===== ARTIST/OWNER VIEW =====

  // Get active licenses
  const activeLicenses = licenses.filter(isLicenseActive);

  // Get the most recent active license (by createdAt)
  const mostRecentActiveLicense = activeLicenses.length > 0
    ? activeLicenses.sort((a, b) => b.createdAt - a.createdAt)[0]
    : null;

  // Determine if track is licensed based on active licenses
  const isLicensed = activeLicenses.length > 0;

  // Get expiry info from most recent active license
  const expiryDate = mostRecentActiveLicense?.dateExpires;
  const licenseType = mostRecentActiveLicense?.licenseType || 'none';
  const formattedExpiryDate = expiryDate ? moment(expiryDate).format('DD/MM/YYYY') : '';

  const handleBadgeClick = () => {
    setShowHistoryModal(true);
  };

  const copyToClipboard = async () => {
    if (claimLink) {
      try {
        await navigator.clipboard.writeText(claimLink);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const applyLicense = async (licenseData: LicenseDataWithShare) => {
    try {
      // Create license document in subcollection
      if (currentOrgId && firebase) {
        const { shareTarget, ...licenseFields } = licenseData;

        const fullLicenseData = {
          ...licenseFields,
          createdBy: firebase.auth.currentUser?.uid || '',
          sourceOrgId: currentOrgId,
          // Snapshot fields for claim page display (avoids needing to read track/org docs)
          trackName: track.trackName,
          trackId: track.id,
          sourceOrgName: currentOrgName || 'Unknown Organization',
          // Store client org name if sharing directly to an org
          ...(shareTarget?.name ? { clientOrgName: shareTarget.name } : {}),
          // Audio file snapshots for playback in client org
          mp3Files: track.mp3Files || [],
          wavFiles: track.wavFiles || [],
          defaultMp3Id: track.defaultMp3Id || null,
          // Track metadata snapshots for client display and filtering
          key: track.key || null,
          bpm: track.bpm || null,
          genre: track.genre || null,
          tags: track.tags || [],
          composer: track.composer || [],
          artist: track.artist || null,
          album: track.album || null,
          recordLabel: track.recordLabel || null,
          publisher: track.publisher || null,
          sessionArtists: track.sessionArtists || [],
          trackColor: track.trackColor || null,
        };

        await firebase.createLicense(currentOrgId, track.id, fullLicenseData);

        // If a claim code was generated, show the claim link modal
        if (licenseData.claimCode) {
          const baseUrl = window.location.origin;
          const link = `${baseUrl}/claim/${licenseData.claimCode}`;
          setClaimLink(link);
          setShowClaimLinkModal(true);
        }

        // If shared directly to an org, we could show a success message
        if (licenseData.clientOrgId && shareTarget) {
          console.log(`License shared directly to ${shareTarget.name}`);
        }
      }

      // Also update track's dateExpires for backwards compatibility
      const updates = {
        licenseStatus: {
          isLicensed: licenseData.licenseType !== 'none',
          durationType: licenseData.licenseType,
          startDate: licenseData.dateStart,
          expiryDate: licenseData.dateExpires,
        },
        dateExpires: licenseData.dateExpires,
      };

      updateTrackLicense(track.id, updates);
      setShowLicenseModal(false);
    } catch (error) {
      console.error('Error creating license:', error);
      // Still update the track even if license doc creation fails
      const updates = {
        licenseStatus: {
          isLicensed: licenseData.licenseType !== 'none',
          durationType: licenseData.licenseType,
          startDate: licenseData.dateStart,
          expiryDate: licenseData.dateExpires,
        },
        dateExpires: licenseData.dateExpires,
      };
      updateTrackLicense(track.id, updates);
      setShowLicenseModal(false);
    }
  };

  const handleDeactivateLicense = async (licenseId: string) => {
    if (!currentOrgId || !firebase) return;

    try {
      await firebase.deactivateSingleLicense(currentOrgId, track.id, licenseId);
    } catch (error) {
      console.error('Error deactivating license:', error);
    }
  };

  const handleAddLicenseFromHistory = () => {
    setShowHistoryModal(false);
    setShowLicenseModal(true);
  };

  return (
    <div className="license-status-selector d-flex justify-content-center">
      {isLicensed ? (
        <div className="d-flex flex-column align-items-center">
          <Badge
            className="badge-accent"
            style={{ cursor: 'pointer' }}
            onClick={handleBadgeClick}
            title="Click to view license history"
          >
            Licensed
          </Badge>
          <div className="mt-1">
            <small className="text-white">
              {licenseType === 'blanket' ? 'Blanket' : expiryDate ? `Until ${formattedExpiryDate}` : ''}
            </small>
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-center">
          <Button
            size="sm"
            className="btn-primary-custom btn-ripple"
            onClick={handleBadgeClick}
          >
            License
          </Button>
        </div>
      )}

      {/* License creation modal */}
      <ShareLicenseModal
        isOpen={showLicenseModal}
        onClose={() => setShowLicenseModal(false)}
        onApply={applyLicense}
        trackName={track.trackName}
        trackData={track}
        firebase={firebase}
      />

      {/* License History Modal */}
      <LicenseHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        trackName={track.trackName}
        trackId={track.id}
        licenses={licenses}
        onDeactivateLicense={handleDeactivateLicense}
        onAddLicense={handleAddLicenseFromHistory}
        isLoading={licensesLoading}
        firebase={firebase}
      />

      {/* Claim Link Modal */}
      <Modal
        isOpen={showClaimLinkModal}
        toggle={() => setShowClaimLinkModal(false)}
        style={{
          color: '#ffffff',
          maxWidth: '500px',
          margin: '1.75rem auto',
        }}
        contentClassName="delete-confirm-modal"
      >
        <ModalHeader
          toggle={() => setShowClaimLinkModal(false)}
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            color: '#ffffff',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          close={
            <button
              className="close"
              onClick={() => setShowClaimLinkModal(false)}
              style={{
                color: 'var(--accent-1-color)',
                opacity: 1,
                textShadow: 'none',
                background: 'none',
                border: 'none',
                fontSize: '1.75rem',
                fontWeight: 'normal',
                cursor: 'pointer',
              }}
            >
              &times;
            </button>
          }
        >
          <span style={{ color: '#fff' }}>License Created - Share Link</span>
        </ModalHeader>
        <ModalBody
          style={{
            backgroundColor: 'var(--chatbot-color-1)',
            color: '#ffffff',
            padding: '1.5rem',
          }}
        >
          <p style={{ marginBottom: '1rem' }}>
            Your license has been created. Share this link with the recipient to allow them to claim the license:
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <input
              type="text"
              value={claimLink || ''}
              readOnly
              style={{
                flex: 1,
                backgroundColor: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '0.9rem',
                outline: 'none',
              }}
            />
            <Button
              size="sm"
              className="btn-primary-custom"
              onClick={copyToClipboard}
              style={{ whiteSpace: 'nowrap' }}
            >
              {copySuccess ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            The recipient will need to sign in or create an account to claim this license.
          </p>
        </ModalBody>
        <ModalFooter
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            className="btn-info-custom"
            onClick={() => {
              setShowClaimLinkModal(false);
              setClaimLink(null);
            }}
          >
            <span style={{ position: 'relative', zIndex: 2 }}>Done</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default LicenseStatusSelector;
